import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ReEvent, ExperienceLevel, Id, ReSession, SessionStatus, SessionType, UserDetails, RolesForEvent } from "../../interfaces/types";
import { useCustomThemeContext } from "../../providers/customThemeProvider";
import { useEventContext } from "../../providers/EventProvider";
import { Autocomplete, Avatar, Box, Button, Chip, Container, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Slider, Switch, TextField, Typography } from "@mui/material";

import EventSubmitButton from "../buttons/EventSubmitButton";
import { fetchUsers } from "../../services/firebaseService";
import { useUserContext } from "../../providers/userProvider";
import uuidv4 from "uuid-random";
import InfoTooltip from "../common/InfoTooltip/InfoTooltip";
import { useSessionContext } from "../../providers/SessionProvider";
import { Clear, Laptop } from "@mui/icons-material";
import { grey } from "@mui/material/colors";

interface SessionFormProps {
    editForm?: boolean;
    submitButtonText: string;
    maxPresenters?: number;
    sessionToUpdate?: ReSession;
}



export const SessionForm: FC<SessionFormProps> = ({ editForm = false, submitButtonText, maxPresenters = 2, sessionToUpdate }) => {
    const navigate = useNavigate();
    const { eventId } = useParams();
    const { addSession, updateSession } = useSessionContext();
    const { upcomingEvents = [], getEventBySessionId, getEventById, isXpFactorActive } = useEventContext();
    const { theme, updateTheme } = useCustomThemeContext();
    const { userDetails, updateEventRoleOnUsers, getUserById } = useUserContext();
    const [selectedEvent, setSelectedEvent] = useState<ReEvent>();
    const [isBeingSubmitted, setIsBeingSubmitted] = useState(false);
    const [users, setUsers] = useState<UserDetails[]>([]);
    const [session, setSession] = useState<ReSession>();
    const [selectedPresenters, setSelectedPresenters] = useState<UserDetails[]>([]);
    const [prevPresenters, setPrevPresenters] = useState<UserDetails[]>([]);

    const defaultSession = {
        id: uuidv4(),
        title: "",
        subtitle: "",
        shortDescription: "",
        longDescription: "",
        topic: selectedEvent?.eventConfiguration?.topics[0]?.id as Id,
        type: SessionType.HANDS_ON,
        status: SessionStatus.DRAFT,
        duration: selectedEvent?.eventConfiguration?.possibleDurations[0]?.id,
        experienceLevel: ExperienceLevel.NO_EXPERIENCE,
        intendedAudience: "",
        laptopsRequired: false,
        materialDescription: "",
        materialUrl: "",
        materialsNeeded: "",
        maxParticipants: 0,
        otherLimitations: "",
        outlineOrTimetable: "",
        roomSetup: selectedEvent?.eventConfiguration?.roomSetups[0]?.id,
        sessionGoal: "",
        xpFactor: 0,
        createdAt: new Date(),
        createdBy: "" as Id,
        updatedAt: new Date(),
        updatedBy: "" as Id,
        presenterIds: [] as Id[],
    } as ReSession;


    useEffect(() => {
        if (eventId) {
            const fetchedEvent = getEventById(eventId);
            if (fetchedEvent) {
                setSelectedEvent(fetchedEvent);
                updateTheme(fetchedEvent);
            }
        }
    }, [eventId, getEventById]);

    useEffect(() => {
        const eventFromNavigation = eventId ? getEventById(eventId) : undefined;
        //to set the selected event and thereby trigger the update theme effect
        if (eventFromNavigation) {
            setSelectedEvent(eventFromNavigation);
        }
        else if (editForm && sessionToUpdate) {
            setSelectedEvent(getEventBySessionId(sessionToUpdate.id));
        }
        


        fetchUsers()
            .then((fetchedUsers) => {
                if (fetchedUsers) {
                    setUsers(fetchedUsers);
                }
            })
            .then(() => {
                setUsers((prevUsers) => prevUsers.sort((a, b) => a.firstName.localeCompare(b.firstName)));
            });
    }, []);

    useEffect(() => {
        if (userDetails) {
            if (editForm) {
                //set the session to the session that is being edited
                if (sessionToUpdate) {
                    const updateSession = { ...sessionToUpdate };
                    setSession(updateSession);
                    const presenters = sessionToUpdate.presenterIds.map((presenterId) => getUserById(presenterId)).filter((presenter): presenter is UserDetails => presenter !== undefined);
                    setPrevPresenters(presenters);
                    setSelectedPresenters(presenters);
                    setSelectedEvent(getEventBySessionId(sessionToUpdate.id));
                }
            }
            else {
                setSession({
                    ...defaultSession,
                    createdBy: userDetails.uid as Id,
                    updatedBy: userDetails.uid as Id,
                } as ReSession);
            }
        }
    }, [userDetails, sessionToUpdate, selectedEvent]); //added selectedEvent to the dependencies to make sure the session gets the right topic, duration and roomSetup as default

    const handleSubmit = async (e: React.FormEvent) => {
        setIsBeingSubmitted(true);
        {/*console.log("Submitting session");
        console.log(session);
        console.log(selectedEvent);
        console.log(session?.title);
        console.log(session?.subtitle);
        console.log(session?.topic);
        console.log(session?.type);
        console.log(session?.shortDescription);
        console.log(session?.roomSetup);
        console.log(session?.duration);
        console.log(session?.presenterIds, session?.presenterIds.length);*/}

        if (
            selectedEvent &&
            session &&
            session.title &&
            session.subtitle &&
            session.topic &&
            session.type &&
            session.shortDescription &&
            session.roomSetup &&
            session.duration && 
            session.presenterIds.length > 0
        ) {
            if (editForm) {
                const updatedSession = {
                    ...session,
                    updatedAt: new Date(),
                    updatedBy: userDetails?.uid as string,
                };
                setSession(updatedSession);
                updateEventRoleOnUsers(selectedEvent.id, RolesForEvent.SUBMITTER, prevPresenters, selectedPresenters);
                //make sure the creater keeps the role of submitter if they are not chosen as a presenter
                if (session.createdBy && !selectedPresenters.some(presenter => presenter.uid === session.createdBy)) {
                    const createdByUser = getUserById(session.createdBy);
                    if (createdByUser) {
                        updateEventRoleOnUsers(selectedEvent.id, RolesForEvent.SUBMITTER, [], [createdByUser]);
                    }
                };
                updateSession(updatedSession);
                setIsBeingSubmitted(false);
            }
            else {
                const newSession = {
                    ...session,
                    id: uuidv4(),
                    createdAt: new Date(),
                    createdBy: userDetails?.uid as string,
                    updatedAt: new Date(),
                    updatedBy: userDetails?.uid as string,
                };
                setSession(newSession);

                updateEventRoleOnUsers(selectedEvent.id, RolesForEvent.SUBMITTER, prevPresenters, selectedPresenters);
                //give the creater the role of submitter if they are not chosen as a presenter
                if (session.createdBy && !selectedPresenters.some(presenter => presenter.uid === session.createdBy)) {
                    const createdByUser = getUserById(session.createdBy);
                    if (createdByUser) {
                        updateEventRoleOnUsers(selectedEvent.id, RolesForEvent.SUBMITTER, [], [createdByUser]);
                    }
                };
                addSession(newSession, selectedEvent);
                setIsBeingSubmitted(false);
            }
            navigate("/sessionlist/" + selectedEvent.id);


        } else {
            //console.log("Session or selectedEvent is undefined");
            setIsBeingSubmitted(false);
        }
    };

    return (
        <Container disableGutters>
            {upcomingEvents.length === 0 && <Box sx={{ mt: 3, width: (editForm ? "100%" : "70%"), justifySelf: "center", textAlign: "center" }}>
                <Typography variant="h5" mt={3}>There are no upcoming events to create a session for at the moment!</Typography>
                <Button sx={{ mt: 3 }} onClick={() => navigate("/")} variant="contained" color="primary">Return to Home</Button>
            </Box>}

            {upcomingEvents.length > 0 &&
                <Box sx={{ mt: 3, width: "100%", justifySelf: "center", textAlign: "center" }}>
                    <Typography variant="h5" mt={3}>Session Details</Typography>

                    <Container disableGutters
                        sx={{
                            mt: 3,
                            border: 1,
                            borderColor: theme.palette.primary.main,
                            p: 2,
                            textAlign: "left",
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                        }}>

                        {/*event logo and name*/}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {selectedEvent?.eventConfiguration?.logoUrl && (
                                <img
                                    src={selectedEvent.eventConfiguration.logoUrl}
                                    alt={selectedEvent.name}
                                    style={{ width: "200px", height: "100px", objectFit: 'contain', marginRight: "3rem" }}
                                />
                            )}
                            <Typography variant="h6">{selectedEvent?.name}</Typography>
                        </Box>
                        {/*select an upcoming event*/}
                        {/*<FormControl fullWidth>
                            <InputLabel required id="event-select-label">Event</InputLabel>
                            <Select
                                labelId="event-select-label"
                                id="event-select"
                                value={selectedEvent ? upcomingEvents.indexOf(selectedEvent) : ""}
                                label="Event"
                                required
                                onChange={(e) => {
                                    const chosenEvent = upcomingEvents?.[e.target.value as number];
                                    if (chosenEvent) {
                                        setSelectedEvent(chosenEvent);
                                    }
                                }}
                            >
                                {upcomingEvents?.map((event, index) => (
                                    <MenuItem key={event.id} value={index}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {event?.eventConfiguration?.logoUrl && (
                                                <img
                                                    src={event.eventConfiguration.logoUrl}
                                                    alt={event.name}
                                                    style={{ width: "200px", height: "100px", objectFit: 'contain', marginRight: "3rem" }}
                                                />
                                            )}
                                            <Typography variant="h6">{event.name}</Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>*/}

                        {/*presenters*/}
                        <FormControl fullWidth>
                            <Autocomplete
                                multiple
                                spellCheck
                                id="presenterSelection"
                                value={selectedPresenters}
                                onChange={(event, newValue) => {
                                    if (newValue.length <= maxPresenters && session) { // Check against max presenters
                                        setSession({
                                            ...session,
                                            presenterIds: [
                                                ...newValue.map((presenter) => presenter.uid),
                                            ],
                                        });
                                        setSelectedPresenters([...newValue]);
                                    }
                                }}
                                options={selectedPresenters.length < maxPresenters ? users : []}
                                getOptionLabel={(option) => `${option?.firstName ?? ""} ${option?.lastName ?? ""}`}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => {
                                        const { key, ...tagProps } = getTagProps({ index });
                                        return (
                                            <Chip
                                                avatar={<Avatar alt={"avatar van " + option?.firstName + " " + option?.lastName} src={option.profileImageUrl} />}
                                                key={uuidv4()}
                                                label={option?.firstName + " " + option?.lastName}
                                                {...tagProps}
                                                variant="outlined"
                                            />
                                        );
                                    })
                                }
                                renderInput={(params) => (
                                    <TextField {...params} label="Presented by" placeholder={selectedPresenters.length >= maxPresenters ? "" : "Add Co-Presenter"} />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={uuidv4()}>
                                        <Avatar alt={"avatar van " + option?.firstName + "" + option?.lastName} src={option.profileImageUrl} sx={{ mr: 2 }} />
                                        {option?.firstName} {option?.lastName}
                                    </li>
                                )}
                                noOptionsText={selectedPresenters.length >= maxPresenters ? "Maximum amount of co-presenters reached" : "No options"}
                            /></FormControl>

                        {/*session title */}
                        <FormControl fullWidth>
                            <TextField
                                id="session-title"
                                label="Session Title"
                                value={session?.title || ""}
                                required
                                slotProps={
                                    { htmlInput: { maxLength: 70 }, }
                                }
                                onChange={(e) => {
                                    if (session) {
                                        setSession({
                                            ...session,
                                            title: e.target.value,
                                        });
                                    }
                                }}
                            />
                        </FormControl>
                        {/*session subtitle */}
                        <FormControl fullWidth>
                            <TextField
                                id="session-subtitle"
                                label="Session Subtitle"
                                value={session?.subtitle || ""}
                                required
                                slotProps={
                                    { htmlInput: { maxLength: 80 }, }
                                }
                                onChange={(e) => {
                                    if (session) {
                                        setSession({
                                            ...session,
                                            subtitle: e.target.value,
                                        });
                                    }
                                }}
                            />
                        </FormControl>
                        {/*session topic and type */}
                        <Box sx={{ display: "flex", gap: 2 }}>
                            {/*session topic */}
                            <FormControl sx={{ flex: "1 1 50%" }}>
                                <InputLabel required id="session-topic-select-label">Session Topic</InputLabel>
                                <Select
                                    labelId="session-topic-select-label"
                                    id="session-topic-select"
                                    value={session?.topic || selectedEvent?.eventConfiguration?.topics[0]?.id || ""}
                                    label="Session Topic"
                                    required
                                    onChange={(e) => {
                                        if (session && selectedEvent?.eventConfiguration?.topics) {
                                            setSession({
                                                ...session,
                                                topic: e.target.value as Id,
                                            });
                                        }
                                    }}
                                >
                                    {selectedEvent?.eventConfiguration?.topics?.sort((a, b) => a.name.toString().localeCompare(b.name.toString())).map((topic) => (
                                        <MenuItem key={topic.id} value={topic.id}>
                                            <Typography variant="body1">{topic.name}</Typography>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/*session type */}
                            <FormControl sx={{ flex: "1 1 50%" }}>
                                <InputLabel required id="session-type-select-label">Session Type</InputLabel>
                                <Select
                                    labelId="session-type-select-label"
                                    id="session-type-select"
                                    value={session?.type || ""}
                                    label="Session Type"
                                    required
                                    onChange={(e) => {
                                        if (session) {
                                            setSession({
                                                ...session,
                                                type: e.target.value as SessionType,
                                            });
                                        }
                                    }}
                                >
                                    {Object.values(SessionType).sort((a, b) => a.toString().localeCompare(b.toString())).map((type) => (
                                        <MenuItem key={type} value={type}>
                                            <Typography variant="body1">{type}</Typography>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        {/*short description */}
                        <FormControl sx={{ flex: "1 1 50%" }}>
                            <TextField
                                required
                                rows={5}
                                multiline
                                id="session-short-description"
                                label="Short Description"
                                value={session?.shortDescription || ""}
                                onChange={(e) => {
                                    if (session) {
                                        setSession({
                                            ...session,
                                            shortDescription: e.target.value,
                                        });
                                    }
                                }}
                            />
                        </FormControl>

                        {/*long description */}
                        <FormControl sx={{ flex: "1 1 50%" }}>
                            <TextField
                                rows={5}
                                multiline
                                id="session-longddescription"
                                label="Long Description"
                                value={session?.longDescription}

                                onChange={(e) => {
                                    if (session) {
                                        setSession({
                                            ...session,
                                            longDescription: e.target.value,
                                        });
                                    }
                                }}
                            />
                        </FormControl>

                        {/*session goal */}
                        <FormControl sx={{ flex: "1 1 50%" }}>
                            <TextField
                                rows={3}
                                multiline
                                id="session-goal"
                                label="Session Goal"
                                value={session?.sessionGoal || ""}
                                placeholder="what do you expect participants to get out of this session?"
                                onChange={(e) => {
                                    if (session) {
                                        setSession({
                                            ...session,
                                            sessionGoal: e.target.value,
                                        });
                                    }
                                }}
                            />
                        </FormControl>
                        {/*xp-factor, experience level and laptops required */}
                        <Box sx={{ display: "flex", gap: 5 }}>
                            {/*xp-factor */}
                            {selectedEvent && isXpFactorActive(selectedEvent) && <FormControl sx={{ flex: "1 1 33%" }}>
                                <Typography id="xp-factor-slider-label" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                    XP factor: <br />{session?.xpFactor}
                                    <InfoTooltip message="The XP factor indicates the extremeness of your session. How thought-provoking, exiting, eXtreme,... is your session?" />
                                </Typography>
                                <Slider
                                    aria-labelledby="xp-factor-slider-label"
                                    valueLabelDisplay="off" //do not display value on the slider itself
                                    value={session?.xpFactor || 0}
                                    min={0}
                                    max={10}
                                    step={1}
                                    marks
                                    onChange={(e, value) => {
                                        if (session) {
                                            setSession({
                                                ...session,
                                                xpFactor: value as number,
                                            });
                                        }
                                    }}
                                />
                            </FormControl>}

                            {/*experience level */}
                            <FormControl sx={{ flex: "1 1 33%" }}>
                                <Typography id="exp-slider-label" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                    Experience Level: <br />{session?.experienceLevel}
                                </Typography>
                                <Slider
                                    aria-labelledby="exp-slider-label"
                                    valueLabelDisplay="off" //do not display value on the slider itself
                                    value={Object.values(ExperienceLevel).indexOf(session?.experienceLevel as ExperienceLevel) || 0}
                                    min={0}
                                    max={4}
                                    step={1}
                                    marks
                                    onChange={(e, value) => {
                                        if (session) {
                                            setSession({
                                                ...session,
                                                experienceLevel: Object.values(ExperienceLevel)[value as number] as ExperienceLevel,
                                            });
                                        }
                                    }}
                                />
                            </FormControl>
                            {/*laptops required */}
                            <FormControl sx={{ flex: "1 1 33%" }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={session?.laptopsRequired || false}
                                            onChange={(e) => {
                                                if (session) {
                                                    setSession({
                                                        ...session,
                                                        laptopsRequired: e.target.checked,
                                                    });
                                                }
                                            }}
                                            inputProps={{ 'aria-labelledby': 'laptops-required-switch-label' }}
                                            icon={<Clear sx={{ p: 0.25, color: theme.palette.primary.main, backgroundColor: grey[300], borderRadius: '50%', fontSize: 16 }} />} // Icon when switch is off
                                            checkedIcon={<Laptop sx={{ p: 0.25, color: 'white', backgroundColor: theme.palette.primary.main, borderRadius: '50%', fontSize: 16 }} />} // Icon when switch is on
                                            sx={{ transform: 'scale(1.5)', mr: 2 }} // Make the switch bigger and add margin-right for space
                                        />
                                    }
                                    label="Laptops Required"
                                    labelPlacement="top" />
                            </FormControl>
                        </Box>
                        {/*max participants and intended audience */}
                        <Box sx={{ display: "flex", gap: 2 }}>
                            {/*max participants*/}
                            <FormControl sx={{ flex: "1 1 50%" }}>
                                <TextField
                                    required
                                    id="outlined-number"
                                    label="Max Participants"
                                    placeholder="Max Participants"
                                    type="number"
                                    value={session?.maxParticipants || 0}
                                    slotProps={{
                                        inputLabel: {
                                            shrink: true,
                                        },
                                        htmlInput: {
                                            min: 0,
                                            max: 1000,
                                        },
                                    }}
                                    onChange={(e) => {
                                        if (session) {
                                            setSession({
                                                ...session,
                                                maxParticipants: parseInt(e.target.value),
                                            });
                                        }
                                    }}
                                />
                            </FormControl>

                            {/*intended audience */}
                            <FormControl sx={{ flex: "1 1 50%" }}>
                                <TextField
                                    id="intended-audience"
                                    label="Intended Audience"
                                    value={session?.intendedAudience || ""}
                                    placeholder="who will benefit from this session?"
                                    onChange={(e) => {
                                        if (session) {
                                            setSession({
                                                ...session,
                                                intendedAudience: e.target.value,
                                            });
                                        }
                                    }}
                                />
                            </FormControl>
                        </Box>
                        {/*session roomSetup and duration */}
                        <Box sx={{ display: "flex", gap: 2 }}>
                            {/*room setup */}
                            <FormControl sx={{ flex: "1 1 50%" }}>
                                <InputLabel required id="room-setup-select-label">Room Setup</InputLabel>
                                <Select
                                    labelId="room-setup-select-label"
                                    id="room-setup-select"
                                    value={session?.roomSetup || selectedEvent?.eventConfiguration?.roomSetups?.[0]?.id || ""}
                                    label="Room Setup"
                                    required
                                    onChange={(e) => {
                                        if (session && selectedEvent?.eventConfiguration?.roomSetups) {
                                            setSession({
                                                ...session,
                                                roomSetup: e.target.value as Id,
                                            });
                                        }
                                    }}
                                >
                                    {selectedEvent?.eventConfiguration?.roomSetups?.sort((a, b) => a.name.toString().localeCompare(b.name.toString())).map((setup) => (
                                        <MenuItem key={setup.id} value={setup.id}>
                                            <Typography variant="body1">{setup.name}</Typography>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/*duration */}
                            <FormControl sx={{ flex: "1 1 50%" }}>
                                <InputLabel required id="duration-select-label">Duration</InputLabel>
                                <Select
                                    labelId="duration-select-label"
                                    id="duration-select"
                                    value={session?.duration || selectedEvent?.eventConfiguration?.possibleDurations?.[0]?.id || ""}
                                    label="Duration"
                                    required
                                    onChange={(e) => {
                                        if (session && selectedEvent?.eventConfiguration?.possibleDurations) {
                                            setSession({
                                                ...session,
                                                duration: e.target.value as Id,
                                            });
                                        }
                                    }}
                                >
                                    {selectedEvent?.eventConfiguration?.possibleDurations?.sort((a, b) => a.duration - b.duration).map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            <Typography variant="body1">{option.duration}</Typography>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        {/*materials needed */}
                        <FormControl sx={{ flex: "1 1 50%" }}>
                            <TextField
                                id="materials-needed"
                                label="Materials Needed"
                                value={session?.materialsNeeded || ""}
                                placeholder="Extra stuff you need the organisers to arrange for your session"
                                onChange={(e) => {
                                    if (session) {
                                        setSession({
                                            ...session,
                                            materialsNeeded: e.target.value,
                                        });
                                    }
                                }}
                            />
                        </FormControl>
                        {/*material url */}
                        <FormControl sx={{ flex: "1 1 50%" }}>
                            <TextField
                                id="material-url"
                                label="Material URL"
                                value={session?.materialUrl || ""}
                                placeholder="Absolute URL to your session materials"
                                onChange={(e) => {
                                    if (session) {
                                        setSession({
                                            ...session,
                                            materialUrl: e.target.value,
                                        });
                                    }
                                }}
                            />
                        </FormControl>
                        {/*material description */}
                        <FormControl sx={{ flex: "1 1 50%" }}>
                            <TextField
                                rows={4}
                                multiline
                                id="material-description"
                                label="Material URL Description"
                                value={session?.materialDescription || ""}
                                placeholder="Descriptive text for the link to your session materials"
                                onChange={(e) => {
                                    if (session) {
                                        setSession({
                                            ...session,
                                            materialDescription: e.target.value,
                                        });
                                    }
                                }}
                            />
                        </FormControl>
                        {/*other limitations and outline or timetable */}
                        <Box sx={{ display: "flex", gap: 2 }}>
                            {/*other limitations */}
                            <FormControl sx={{ flex: "1 1 50%" }}>
                                <TextField
                                    rows={4}
                                    multiline
                                    id="other-limitations"
                                    label="Other Limitations"
                                    value={session?.otherLimitations || ""}
                                    onChange={(e) => {
                                        if (session) {
                                            setSession({
                                                ...session,
                                                otherLimitations: e.target.value,
                                            });
                                        }
                                    }}
                                />
                            </FormControl>

                            {/*outline or timetable */}
                            <FormControl sx={{ flex: "1 1 50%" }}>
                                <TextField
                                    rows={4}
                                    multiline
                                    id="outline-timetable"
                                    label="Outline or Timetable"
                                    value={session?.outlineOrTimetable || ""}
                                    onChange={(e) => {
                                        if (session) {
                                            setSession({
                                                ...session,
                                                outlineOrTimetable: e.target.value,
                                            });
                                        }
                                    }}
                                />
                            </FormControl>
                        </Box>

                    </Container>
                    <EventSubmitButton isBeingSubmitted={isBeingSubmitted} handleSubmit={handleSubmit} text={submitButtonText} />
                </Box>}
        </Container>
    );
};

export default SessionForm;